import React from 'react';
import { graphql } from 'react-apollo';
import { useQuery } from '@apollo/react-hooks';
import compose from 'lodash/flowRight';
import { useLocation } from 'react-router-dom';
import CURRENT_USER from '../../../../GraphQl/Queries/CURRENT_USER';
import CAMPAIGN_ORDER_BASKET_ITEMS from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_BASKET_ITEMS';
import CAMPAIGN_ORDER_TRANSACTIONS_BASKET from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTIONS_BASKET';
import CAMPAIGN_ORDER_BASKET_BY_DATE from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_BASKET_BY_DATE';
import moment from 'moment-timezone';
import { useTimezone } from '../../../../hooks/useTimezone';

const BasketItemsTabLoader = ({ children, ...props }) => {
  const { campaignOrder } = props;
  const location = useLocation();
  const { timezone: effectiveTimezone = 'UTC' } = useTimezone();

  // Read pagination and filters from URL params
  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;
  const pageSize = parseInt(queryParams.get('pageSize')) || 10;
  const searchTerm = queryParams.get('searchTerm') || '';
  const startDateParam = queryParams.get('startDate');
  const endDateParam = queryParams.get('endDate');

  // Convert dates to timestamps only if they exist
  const startDateFilter = startDateParam ? startDateParam : null;
  const endDateFilter = endDateParam ? endDateParam : null;
  const hasDateFilter = startDateFilter && endDateFilter ? true : false;

  // Build timezone-aware date filters
  const getStartDateISO = () => {
    if (!startDateFilter) return null;
    const startMoment = effectiveTimezone === 'UTC'
      ? moment.utc(startDateFilter).startOf('day')
      : moment.tz(startDateFilter, effectiveTimezone).startOf('day');
    return startMoment.toISOString();
  };

  const getEndDateISO = () => {
    if (!endDateFilter) return null;
    const endMoment = effectiveTimezone === 'UTC'
      ? moment.utc(endDateFilter).endOf('day')
      : moment.tz(endDateFilter, effectiveTimezone).endOf('day');
    return endMoment.toISOString();
  };

  // Build where clause for basket items
  const basketItemsWhere = hasDateFilter
    ? {
        AND: [
          {
            transactionReport: {
              analytics: {
                campaignOrder: {
                  id: campaignOrder.id,
                },
              },
            },
          },
          {
            transaction: {
              transactionDate_gte: getStartDateISO(),
              transactionDate_lte: getEndDateISO(),
              attrWindow_lte: 30,
            },
          },
        ],
      }
    : {
        transactionReport: {
          analytics: {
            campaignOrder: {
              id: campaignOrder.id,
            },
          },
        },
      };

  // Build where clause for transactions basket
  const buildTransactionsBasketWhere = () => {
    const baseWhere = {
      campaignOrderTransactionReport: {
        analytics: {
          campaignOrder: {
            id: campaignOrder.id,
          },
        },
      },
    };

    const conditions = [baseWhere];

    // Add date filter if present
    if (hasDateFilter) {
      conditions.push({
        transactionDate_gte: getStartDateISO(),
        transactionDate_lte: getEndDateISO(),
        attrWindow_lte: 30,
      });
    }

    // Add search filter if present
    if (searchTerm) {
      conditions.push({
        orderId_contains: searchTerm,
      });
    }

    return conditions.length > 1 ? { AND: conditions } : baseWhere;
  };

  const transactionsBasketWhere = buildTransactionsBasketWhere();

  const { data: basketItemsData, loading: basketItemsDataLoading } = useQuery(
    CAMPAIGN_ORDER_BASKET_ITEMS,
    {
      variables: {
        where: basketItemsWhere,
        orderBy: 'total_DESC',
      },
    }
  );

  const { data: basketItemsGroupedOrderId, loading: basketItemsGroupedLoading } = useQuery(
    CAMPAIGN_ORDER_TRANSACTIONS_BASKET,
    {
      variables: {
        where: transactionsBasketWhere,
        first: pageSize,
        skip: (Number(currentPage) - 1) * pageSize,
        orderBy: 'transactionDate_ASC',
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  );

  const { data: basketItemsByDate } = useQuery(CAMPAIGN_ORDER_BASKET_BY_DATE, {
    variables: {
      id: campaignOrder.id,
    },
  });

  return React.cloneElement(children, {
    campaignOrder,
    pageSize,
    basketItemsData: basketItemsData?.campaignOrderTransactionsBasketItemsesConnection?.edges?.map(
      (o) => ({ ...o.node })
    ),
    basketItemsGroupedOrderId:
      basketItemsGroupedOrderId?.campaignOrderTransactionsWithEventsesConnection?.edges?.map(
        (o) => ({ ...o.node })
      ),
    basketItemsGroupedTotalCount: basketItemsGroupedOrderId?.full?.aggregate?.count || 0,
    currentPage,
    basketItemsDataLoading: basketItemsDataLoading || basketItemsGroupedLoading,
    basketItemsByDate: basketItemsByDate?.campaignOrderTransactionsBasketItemsByDates,
    ...props,
  });
};

export default compose(
  graphql(CURRENT_USER, {
    props: ({ data: { currentUser }, ownProps }) => {
      return ownProps.currentUser || { currentUser };
    },
  })
)(BasketItemsTabLoader);
